import axios from "axios";
import { pick } from "lodash";

import {
    dispatchActiveClinicClean,
    dispatchActiveClinicSave,
} from "../store/reducers/activeClinicReducer";
import { dispatchLogin } from "../store/reducers/authReducer";

export const getAllClinics = (params) => {
    return async () => {
        return axios
            .get("/api/managers/clinics/", { params: params })
            .then((response) => {
                return response.data;
            })
            .catch(() => {});
    };
};

export const getClinic = (id) => {
    return async (...args) => {
        const { navigate } = args[2];

        if (isNaN(id)) {
            navigate(`/unknown?entity=clinic&id=${id}`);
            return;
        }

        return axios
            .get(`/api/managers/clinics/${id}`)
            .then((response) => {
                return response.data.content;
            })
            .catch((error) => {
                if ([403, 404].includes(error?.response?.status))
                    navigate(`/unknown?entity=clinic&id=${id}`);
            });
    };
};

export const getPublicClinic = (params) => {
    return async (...args) => {
        const { navigate } = args[2];

        return axios
            .get("/api/anonymous/clinic", { params: params })
            .then((response) => {
                const data = response.data.content;

                return {
                    ...data.clinic_data,
                    schedule: data.schedule,
                    logo: data.logo_url,
                };
            })
            .catch((error) => {
                if ([403, 404].includes(error?.response?.status))
                    navigate(`/unknown?entity=publicClinic&id=${params.clinic_id}`);
            });
    };
};

export const postClinic = (data, managerId) => {
    return async (...args) => {
        const dispatch = args[0];
        const { navigate } = args[2];
        const { add_schedule: _add_schedule, ...reqData } = data;

        return axios
            .post("/api/managers/clinics/", { ...reqData, manager_id: managerId })
            .then((response) => {
                const clinics = response.data.content.clinics;
                const clinicId = Object.keys(clinics).find(
                    (key) => clinics[key].name === data.name,
                );
                const clinic = clinics[clinicId];
                navigate(`/clinic/${clinicId}`);
                dispatchActiveClinicSave(dispatch, clinicId, clinic.name, clinic.status);
                dispatchLogin(dispatch, response.data.content);
            })
            .catch(() => {});
    };
};

export const putClinic = (data, id, disableEdit) => {
    const reqData = pick(data, [
        "address",
        "town",
        "name",
        "email",
        "mobile_phone",
        "landline_phone",
        "invoice_code",
        "invoice_number",
        "cif",
        "task_generation",
        "online_booking",
        "password",
        "schedule",
        "future_debt",
    ]);
    return async () => {
        return axios
            .put(`/api/managers/clinics/${id}`, { new_info: reqData })
            .then(() => {
                disableEdit();
            })
            .catch(() => {});
    };
};

export const deleteClinic = (id) => {
    return async (...args) => {
        const dispatch = args[0];
        const { navigate } = args[2];

        return axios
            .delete(`/api/managers/clinics/${id}`)
            .then((response) => {
                dispatchLogin(dispatch, response.data.content);
                dispatchActiveClinicClean(dispatch);
                navigate("/clinic");
            })
            .catch(() => {});
    };
};

// Logo
export const updateLogo = (data, clinicId, refreshLogoUrl) => {
    return async () => {
        const formData = new FormData();
        formData.append("clinic_id", clinicId);
        formData.append("logo", data.logo[0]);

        return axios
            .post(`/api/managers/clinics/${clinicId}/logo`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            })
            .then((response) => {
                refreshLogoUrl(response.data.content.logo_url);
            })
            .catch(() => {});
    };
};

export const deleteLogo = (clinicId, refreshLogoUrl) => {
    return async () => {
        return axios
            .delete(`/api/managers/clinics/${clinicId}/logo`)
            .then(() => {
                refreshLogoUrl(undefined);
            })
            .catch(() => {});
    };
};

// Patient History Template
export const getClinicPatientHistoryTemplate = (clinicId) => {
    return async () => {
        return axios
            .get(`/api/managers/clinics/${clinicId}/patient_history_fields`)
            .then((response) => {
                return response.data;
            })
            .catch(() => {});
    };
};

export const putClinicPatientHistoryTemplate = (_, data, clinicId, preview) => {
    const reqData = {};
    Object.keys(data).forEach((key) => {
        const field = data[key];
        reqData[key] = {
            ...pick(field, [
                "condition",
                "name",
                "type",
                "defaultValue",
                "required",
                "options",
                "label",
                "position",
            ]),
        };
    });

    return async () => {
        return axios
            .put(`/api/managers/clinics/${clinicId}/patient_history_fields`, reqData)
            .then(() => {
                if (preview) {
                    preview();
                }
            })
            .catch(() => {});
    };
};

// Schedule
export const getClinicSchedule = (clinicId) => {
    return async () => {
        return axios
            .get(`/api/managers/clinics/${clinicId}/schedule`)
            .then((response) => {
                return { schedule: response.data.content };
            })
            .catch(() => {});
    };
};

export const putClinicSchedule = (data, clinicId, close) => {
    return async () => {
        return axios
            .put(`/api/managers/clinics/${clinicId}/schedule`, { new_info: data.schedule })
            .then(() => {
                close();
            })
            .catch(() => {});
    };
};

// Payment Gateway
export const getClinicPaymentGatewayParams = (params) => {
    const path =
        process.env.NODE_ENV !== "production"
            ? "https://sis-t.redsys.es:25443/sis/realizarPago"
            : "https://sis.redsys.es/sis/realizarPago";

    return async () => {
        return axios
            .get("/api/managers/payment_gateway/generate_request", { params: params })
            .then((response) => {
                return {
                    params: response.data.content,
                    path: path,
                };
            })
            .catch(() => {});
    };
};

// Payment Details
export const getClinicPaymentData = (clinicId) => {
    return async () => {
        return axios
            .get(`/api/managers/clinics/${clinicId}/payment_info`)
            .then((response) => {
                return response.data.content;
            })
            .catch(() => {});
    };
};

// Online Bookings
export const getClinicOnlineBookingsData = (clinicId) => {
    return async () => {
        return axios
            .get(`/api/managers/clinics/${clinicId}/online_booking`)
            .then((response) => {
                return response.data.content;
            })
            .catch(() => {});
    };
};

export const putClinicOnlineBookings = (clinicId, activeBookings, setData) => {
    return async () => {
        return axios
            .put(`/api/managers/clinics/${clinicId}/online_booking`, {
                online_booking: activeBookings,
            })
            .then((response) => {
                setData(response.data.content);
            })
            .catch(() => {});
    };
};

// Profile Context
export const getClinicHasPassword = (clinicId) => {
    return async () => {
        return axios
            .get(`/api/managers/clinics/${clinicId}/clinic_profile`)
            .then((response) => {
                return response.data.content;
            })
            .catch(() => {});
    };
};

export const putClinicPassword = (data, id, setContext) => {
    const reqData = {
        new_info: {
            password: data.new_info_password,
        },
    };

    return async () => {
        return axios
            .put(`/api/managers/clinics/${id}/clinic_profile`, reqData)
            .then((response) => {
                setContext(response.data.content);
            })
            .catch(() => {});
    };
};

export const deleteClinicPassword = (id, setContext) => {
    return async () => {
        return axios
            .delete(`/api/managers/clinics/${id}/clinic_profile`)
            .then((response) => {
                setContext(response.data.content);
            })
            .catch(() => {});
    };
};
